import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Get } from '../../Axios/AxiosFunctions';
import { toast } from 'react-toastify';
import './MainZoom.css';
import Loader from '../../Component/Loader';
import { apiUrl } from '../../config/apiUrl';

/**
 * ZoomCallback component handles the OAuth callback from Zoom.
 * It exchanges the authorization code for access and refresh tokens,
 * stores them in localStorage, and provides user feedback.
 * Navigates to /zoom on success or shows error on failure.
 *
 * @component
 */
const ZoomCallback = () =>
{
  const [ searchParams ] = useSearchParams();
  const navigate = useNavigate();
  const [ isLoading, setIsLoading ] = useState( false );
  const [ error, setError ] = useState( null );

  useEffect( () =>
  {
    /**
     * Authenticates the user with Zoom using the provided authorization code.
     * Calls the backend to exchange the code for tokens, stores them,
     * and navigates the user or displays errors as needed.
     *
     * @async
     * @param {string} code - The authorization code returned by Zoom OAuth.
     * @returns {Promise<void>}
     */
    const authenticateWithZoom = async ( code ) =>
    {
      setIsLoading( true );
      try
      {
        const response = await Get(
          `${apiUrl}/api/v1/zoom/callback?code=${code}`,
        );

        if ( response?.data )
        {
          const { access_token, refresh_token } = response.data;

          localStorage.setItem( 'zoom_access_token', access_token );
          localStorage.setItem( 'zoom_refresh_token', refresh_token );

          toast.success( 'Zoom authentication successful!', {
            position: toast.POSITION.TOP_CENTER,
          } );

          navigate( '/zoom' );
        } else
        {
          throw new Error( 'Invalid response from server' );
        }
      } catch ( error )
      {
        console.error( 'Zoom OAuth error:', error );
        setError(
          error?.response?.data?.message?.error || 'Authentication failed',
        );
        toast.error( 'Failed to authenticate with Zoom', {
          position: toast.POSITION.TOP_CENTER,
        } );
      } finally
      {
        setIsLoading( false );
      }
    };

    const code = searchParams.get( 'code' );
    if ( code )
    {
      authenticateWithZoom( code );
    } else
    {
      setIsLoading( true );
      setError( 'Authorization code not found' );
      toast.error( 'Missing authorization code', {
        position: toast.POSITION.TOP_CENTER,
      } );
      setIsLoading( false );
      navigate( '/zoom' );
    }
  }, [ searchParams, navigate ] );

  if ( isLoading )
  {
    return <Loader />;
  }

  if ( error )
  {
    return (
      <div className="zoom-callback-container">
        <div className="zoom-auth-error">
          <h3>Authentication Error</h3>
          <p>{error}</p>
          <button
            className="retry-btn"
            onClick={() => window.location.reload()}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return null;
};

export default ZoomCallback;
